import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ROUTES } from './routes';

// Two-key "g + X" navigation chords, with a 1s window.
// Skip when the user is typing in an input or editable element.

const CHORDS: Record<string, string> = Object.fromEntries(
  ROUTES.map((r) => [r.chord, r.to]),
);

function isEditable(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function useChordShortcuts() {
  const navigate = useNavigate();
  useEffect(() => {
    let armed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const disarm = () => {
      armed = false;
      if (timer) clearTimeout(timer);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditable(e.target)) return;

      if (!armed && e.key === 'g') {
        armed = true;
        timer = setTimeout(disarm, 1000);
        e.preventDefault();
        return;
      }
      if (armed) {
        const dest = CHORDS[e.key.toLowerCase()];
        disarm();
        if (dest) {
          e.preventDefault();
          navigate({ to: dest });
        }
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      disarm();
    };
  }, [navigate]);
}
